import { useEffect, useState, useMemo } from 'react';

import throttle from 'lodash/throttle';

const DEFAULT_WIDTH = 1366;
const DEFAULT_HEIGHT = 720;

const SCREEN_SIZES_MEDIAS = {
    xs: { maxWidth: 599, minWidth: 0 },
    sm: { maxWidth: 899, minWidth: 600 },
    md: { maxWidth: 1199, minWidth: 900 },
    lg: { maxWidth: 1535, minWidth: 1200 },
    xl: { maxWidth: Number.MAX_SAFE_INTEGER, minWidth: 1536 },
    gtXs: { minWidth: 600 },
    gtSm: { minWidth: 900 },
    gtMd: { minWidth: 1200 },
    gtLg: { minWidth: 1536 },

};

const calculeScreenSizes = windowWidth => {
    return Object.entries(SCREEN_SIZES_MEDIAS)
        .reduce((acc, [key, value]) => {
            const { maxWidth, minWidth } = value;
            return {
                ...acc,
                [key]: (!maxWidth || windowWidth <= maxWidth)
                && (!minWidth || windowWidth >= minWidth),
            };
        }, {});
};

const mapScreenSizeToProps = ({
    sm, md, xs, lg, xl, xxl,
    gtXs, gtSm, gtMd, gtLg,
}) => ({
    isMobile: xs,
    isTablet: sm || md,
    isDesktop: gtMd,
    xs,
    sm,
    md,
    lg,
    xl,
    xxl,
    gtXs,
    gtSm,
    gtMd,
    gtLg,
});

const getScreenSizes = windowWidth => {
    const sizes = calculeScreenSizes(windowWidth);
    return mapScreenSizeToProps(sizes);
};

const useScreen = (throttleWait = 1000) => {
    const [, forceUpdate] = useState(0);

    const hasWindow = typeof window !== 'undefined';

    useEffect(() => {
        if (!hasWindow) return undefined;

        const onResize = throttle(() => {
            forceUpdate(i => i + 1);
        }, throttleWait, { trailing: true });

        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [throttleWait, hasWindow]);

    const windowWidth = hasWindow
        ? window.innerWidth
        || document.documentElement.clientWidth
        || document.body.clientWidth
        : DEFAULT_WIDTH;

    const windowHeight = hasWindow
        ? window.innerHeight
        || document.documentElement.clientHeight
        || document.body.clientHeight
        : DEFAULT_HEIGHT;

    const sizes = useMemo(() => getScreenSizes(windowWidth), [windowWidth]);

    return {
        window: {
            width: windowWidth,
            height: windowHeight,
        },
        sizes,
    };
};

export default useScreen;
